import { Fragment, useContext, useEffect, useMemo, useRef, useState } from "react";
import styled from "@emotion/styled";
import clsx from "clsx";
import {
    booleanProp,
    emailRegExString,
    noEmptyArrayValidation,
    noop,
    noWhitespacesValidation,
    translate,
} from "@mediaspace/shared/utils";
import { Button, Typography } from "@kaltura/ds-react-components";
import {
    FieldType,
    FieldValue,
    RegistrationFieldInfo,
    RegistrationSectionInfo,
} from "@mediaspace/shared/types/RegistrationFieldInfo";
import { Controller, FieldError, useForm } from "react-hook-form";
import { ConfigContext } from "@mediaspace/shared/data-kms-config/ConfigContext";
import ReCAPTCHA from "react-google-recaptcha";
import { isEmpty } from "ramda";
import ReactHtmlParser from "@mediaspace/shared/ui/html-react-parser";
import TextField from "./fieldTypes/TextField";
import CheckboxField from "./fieldTypes/CheckboxField";
import RadioButtonField from "./fieldTypes/RadioButtonField";
import PasswordField from "./fieldTypes/PasswordField";
import SelectField from "./fieldTypes/SelectField";
import FormSection from "./FormSection";
import { passwordValidationFunction } from "@mediaspace/shared/ui";
import CampaignValues from "./CampaignValues";
import { FormFieldError } from "@mediaspace/shared/ui/form/FormElementStyles";
import { RegistrationType } from "./SiteRegistrationProps";
import TagSelectionField from "./fieldTypes/tag-selection-field/TagSelectionField";
import RegistrationTypeField from "./fieldTypes/RegistrationTypeField";
import { ContentContext } from "./ContentContext";

export type UserData = {
    email: string;
    firstName?: string;
    lastName?: string;
    country?: string;
    state?: string;
    company?: string;
};

export interface Props {
    userData: UserData;
    sections: RegistrationSectionInfo[];
    recaptchaSiteKey?: string;
    recaptchaTheme?: "dark" | "light" | undefined;
    recapthcaMode?: "normal" | "invisible" | undefined;
    resetCaptcha?: boolean;
    processing?: boolean;
    registrationType?: RegistrationType;
    onSubmit: (data: Record<string, unknown>) => void;
    className?: string;
    isEmailInSsoDomains?: (email: string, onReturn: () => void) => void;
    ssoDomainsError?: boolean;
    // ID of the <form> tag (could be used to link external buttons)
    formId?: string;
    // Don't render the submit button (because other component will render it externally)
    noSubmitButton?: boolean;
}

const RecaptchaContainer = styled("div")(({ theme }) => ({
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
        marginBottom: theme.spacing(3),
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
    },
}));

const StyledReadOnlyText = styled(Typography)(({ theme }) => ({
    marginTop: theme.spacing(3),
    maxWidth: "58%",
    color: theme.kaltura.palette.tone2,
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
        maxWidth: "100%",
    },
}));

const StyledCaptchaError = styled("div")(({ theme }) => ({
    marginTop: theme.spacing(1),
}));

const StyledSubmitButton = styled(Button)(
    ({ theme, fullWidth }) => ({
        ...(!fullWidth && {
            width: 200,
            [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
                width: "100%",
            },
        }),
    })
);

/**
 * Site registration form
 */
export const RegistrationForm = ({
    userData,
    sections,
    recaptchaSiteKey,
    recapthcaMode,
    recaptchaTheme = "light",
    resetCaptcha = false,
    processing = false,
    registrationType = RegistrationType.NEW,
    onSubmit,
    className,
    isEmailInSsoDomains,
    ssoDomainsError = false,
    formId,
    noSubmitButton = false,
}: Props) => {
    const {
        register,
        control,
        handleSubmit,
        trigger,
        watch,
        setError,
        formState,
        setValue,
        getValues,
        resetField,
    } = useForm<any>({
        criteriaMode: "all",
        reValidateMode: "onChange",
        mode: "all",
    });

    const { errors } = formState as any;

    const config = useContext(ConfigContext);
    const contentContext = useContext(ContentContext) || { fullWidth: true };

    const [isLoadingEmail, setIsLoadingEmail] = useState(false);

    // watch all fields for dependency management
    watch();

    // validate input email is not in sso domains - set error on email field if it is
    useEffect(() => {
        if (ssoDomainsError) {
            setError("registrationinfo.email", {
                type: "ssoDomains",
            });
        }
    }, [setError, ssoDomainsError]);

    const captchaRef = useRef<ReCAPTCHA>(null);

    const firstNameValue = watch("registrationinfo.firstName");
    const lastNameValue = watch("registrationinfo.lastName");

    // password validation
    const passwordValidation = useMemo(() => {
        return passwordValidationFunction(
            firstNameValue,
            lastNameValue,
            config?.passwordValidations
        );
    }, [firstNameValue, lastNameValue]);

    // used for password validation
    const passwordValue = watch("registrationinfo.password");

    useEffect(() => {
        // validate password when entered
        passwordValue && trigger("registrationinfo.password");
    }, [trigger, passwordValue, passwordValidation]);

    // captcha reset
    useEffect(() => {
        if (!resetCaptcha || !recaptchaSiteKey) {
            return;
        }
        captchaRef.current?.reset();
        setCaptchaToken();
        if (recapthcaMode !== "invisible") {
            setValue("captcha", "");
            setError("captcha", {
                type: "required",
                message: "Recaptcha has been reset",
            });
        }

        // we do not want setError() as dependency
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resetCaptcha]);

    //this is a hack since the recaptcha setexpired is not being called when the recaptcha in invisible mode.
    //instead, we went with the recommendation of just reseting it every period.
    //https://stackoverflow.com/questions/55251837/how-to-solve-google-v3-recaptcha-timeout
    useEffect(() => {
        setCaptchaToken();
        let recaptchaInitInterval: number | undefined = undefined;
        if (recaptchaSiteKey && recapthcaMode === "invisible") {
            recaptchaInitInterval = window.setInterval(() => {
                setCaptchaToken();
            }, 110000);
        }
        return () => {
            if (recapthcaMode === "invisible") {
                window.clearInterval(recaptchaInitInterval);
            }
        };
    }, [recapthcaMode, recaptchaSiteKey]);

    const setCaptchaToken = async () => {
        if (recapthcaMode === "invisible") {
            const token = await captchaRef.current?.executeAsync();
            setValue("captcha", token);
        }
    };

    // all the fields - used by error validation focus
    const allFields = useMemo(() => {
        const allFields = sections.map(
            (section: RegistrationSectionInfo, index: number) => section.fields
        );
        return allFields.reduce((a, b) => a.concat(b), []);
    }, [sections]);

    // get field visibility, with dependencies
    const defaultValuesRef = useRef<Record<string, unknown>>({});
    const getVisibleDependency = (field: RegistrationFieldInfo) => {
        // no dependency
        if (
            !field.depends ||
            !field.depends.field ||
            !field.depends.value ||
            isEmpty(field.depends.field)
        ) {
            return true;
        }

        const registrationinfo = getValues().registrationinfo ?? userData;
        const defaultValues = defaultValuesRef.current;

        // this field depends on another field value -
        // we have that field value in the registrationinfo values, or default values
        return registrationinfo && registrationinfo[field.depends.field]
            ? registrationinfo[field.depends.field] === field.depends.value
            : defaultValues &&
                  defaultValues[field.depends.field] === field.depends.value;
    };

    // calculate a field  default value
    const getDefaultValue = (field: RegistrationFieldInfo) => {
        // no default value
        if (!field.defaults) {
            return undefined;
        }

        // true love(depends on nothing)
        const isTrueLove = (defaultOption: {
            defaultValue: FieldValue;
            depends?: { field: string; value: FieldValue };
        }) =>
            !defaultOption.depends ||
            (defaultOption.depends && !defaultOption.depends.field) ||
            (defaultOption.depends && !defaultOption.depends.value);

        // dependency default
        const materialDefault = field.defaults.find(
            (defaultOption: {
                defaultValue: FieldValue;
                depends?: { field: string; value: FieldValue };
            }) => {
                // do we have a field dependency at all
                if (isTrueLove(defaultOption)) {
                    return false;
                }

                // 1. form data
                // 2. user initial data
                const registrationinfo =
                    getValues().registrationinfo ?? userData;

                // 3. default value (need to be previously calculated)
                const defaultValues = defaultValuesRef.current;
                const fieldValue =
                    defaultOption.depends &&
                    (registrationinfo &&
                    registrationinfo[defaultOption.depends.field]
                        ? registrationinfo[defaultOption.depends.field]
                        : defaultValues &&
                          defaultValues[defaultOption.depends.field]);

                // match the dependency by:
                // the value of the field
                // true or false - for fields dependent on checkbox fields
                return (
                    defaultOption.depends &&
                    (fieldValue === defaultOption.depends.value ||
                        fieldValue ===
                            (defaultOption.depends.value === "true") ||
                        (!fieldValue &&
                            defaultOption.depends.value === "false"))
                );
            }
        );

        // true love(depends on nothing) default
        const trueLoveDefault = field.defaults.find(
            (defaultOption: {
                defaultValue: FieldValue;
                depends?: { field: string; value: FieldValue };
            }) => {
                // true love depends on nothing
                return isTrueLove(defaultOption);
            }
        );

        if (materialDefault) {
            // 'cause we are living in..
            return materialDefault.defaultValue;
        }

        // true love wins at the end
        return trueLoveDefault && trueLoveDefault.defaultValue;
    };

    // calculate the initial default values - for inter-dependencies
    const defaultValues = defaultValuesRef.current;
    allFields.forEach((field: RegistrationFieldInfo) => {
        defaultValues[field.name] = getDefaultValue(field);
    });
    defaultValuesRef.current = defaultValues;

    // map dependencies as field => dependant fields
    const fieldDependenciesRef = useRef<
        Record<string, RegistrationFieldInfo[]>
    >({});
    useEffect(() => {
        const fieldDependencies = fieldDependenciesRef.current;
        allFields.forEach((field: RegistrationFieldInfo) => {
            // field dependency + defaults dependencies
            const dependcyFields =
                field.defaults
                    ?.filter((defaultOption) => defaultOption.depends?.field)
                    .map((defaultOption) => defaultOption.depends?.field) ??
                [].concat([field.depends?.field ?? ""]).filter((n) => n);

            // make them unique
            const dependencies = new Set(dependcyFields);

            dependencies.forEach((name) => {
                fieldDependencies[`registrationinfo.${name}`] = [
                    ...(fieldDependencies[`registrationinfo.${name}`] ?? []),
                    field,
                ];
            });
        });

        fieldDependenciesRef.current = fieldDependencies;
    }, []);

    // whenever a form value changes, find all fields that depend on the changed field,
    // calculate their new default and apply it
    useEffect(() => {
        // watch for field changes
        const subscription = watch((_value, { name }) => {
            // get the field dependent fields
            const dependants = fieldDependenciesRef.current[name as string] ?? [];
            dependants.forEach((field: RegistrationFieldInfo) => {
                // calculate new default value for dependent field
                const defaultValue = getDefaultValue(field) ?? null;
                // reset dependent field with new default value
                resetField(`registrationinfo.${field.name}`, {
                    defaultValue: defaultValue,
                });
            });
        });
        return () => subscription.unsubscribe();
    }, [watch]);

    // generate the individual form section fields
    const generateFields = (fields: RegistrationFieldInfo[]) => {
        return fields.map((field: RegistrationFieldInfo) => {
            // field visibility
            const visible =
                getVisibleDependency(field) && !booleanProp(field.hidden);
            if (!visible) {
                return null;
            }
            // field value
            const defaultValue = getDefaultValue(field) ?? null;

            const value = userData && userData[field.name as keyof UserData];

            const errorInfo = errors.registrationinfo?.[
                field.name
            ] as FieldError;
            const errorType = errorInfo?.type;
            let errorMessage = "";
            switch (errorType) {
                case "required":
                    errorMessage = field.mandatoryText;
                    break;
                case "maxLength":
                    errorMessage = translate("Maximum %1 characters allowed", [
                        field.maxLength,
                    ]);
                    break;
            }

            switch (field.fieldType) {
                case FieldType.readOnlyText:
                    return (
                        <StyledReadOnlyText
                            variant={"body2"}
                            key={field.id}
                            className={
                                "kms-ds-site-registration-readonly-field"
                            }
                        >
                            {ReactHtmlParser(field.displayName)}
                        </StyledReadOnlyText>
                    );
                case FieldType.hidden:
                    return (
                        <input
                            key={field.id}
                            type="hidden"
                            {...register("registrationinfo." + [field.name])}
                            readOnly={true}
                            defaultValue={(value || defaultValue) as string}
                            className={"kms-ds-site-registration-hidden-field"}
                        />
                    );
                case FieldType.text: {
                    const registeredField = register(
                        "registrationinfo." + [field.name],
                        noWhitespacesValidation({
                            required: field.mandatory,
                            maxLength: field.maxLength,
                        })
                    );

                    return (
                        <TextField
                            key={field.id}
                            id={field.id}
                            {...registeredField}
                            title={field.displayName}
                            required={field.mandatory}
                            error={errorInfo}
                            errorMessage={errorMessage}
                            placeholder={field.placeHolder}
                            defaultValue={(value || defaultValue) as string}
                            readOnly={booleanProp(field.readOnly)}
                            className={"kms-ds-site-registration-text-field"}
                        />
                    );
                }
                case FieldType.registrationCode:
                    return (
                        <TextField
                            key={field.id}
                            id={field.id}
                            title={field.displayName}
                            {...register(
                                "registrationinfo.registrationCode",
                                noWhitespacesValidation({
                                    required: field.mandatory,
                                    maxLength: field.maxLength,
                                })
                            )}
                            required={field.mandatory}
                            error={
                                errors.registrationinfo
                                    ?.registrationCode as FieldError
                            }
                            errorMessage={errorMessage}
                            placeholder={field.placeHolder}
                            defaultValue={(value || defaultValue) as string}
                            readOnly={booleanProp(field.readOnly)}
                            className={
                                "kms-ds-site-registration-registration-code-field"
                            }
                        />
                    );
                case FieldType.email: {
                    const registeredField = register(
                        "registrationinfo." + [field.name],
                        noWhitespacesValidation({
                            required: true,
                            pattern: emailRegExString,
                            maxLength: field.maxLength,
                        })
                    );

                    return (
                        <TextField
                            key={field.id}
                            id={field.id}
                            title={field.displayName}
                            {...registeredField}
                            required={true}
                            onBlur={(ev) => {
                                registeredField.onBlur(ev);

                                if (
                                    isEmailInSsoDomains &&
                                    !booleanProp(field.readOnly)
                                ) {
                                    setIsLoadingEmail(true);
                                    isEmailInSsoDomains(ev.target.value, () =>
                                        setIsLoadingEmail(false)
                                    );
                                }
                            }}
                            isLoading={isLoadingEmail}
                            errorMessage={
                                errorType === "required"
                                    ? translate("Email address is required")
                                    : errorType === "ssoDomains"
                                    ? translate(
                                          "The email you entered is marked for SSO (Single Sign On), please %1 login %2 first to start the registration process.",
                                          ["<a href='/user/login'>", "</a>"]
                                      )
                                    : errorMessage ||
                                      translate(
                                          "Please use a valid email address"
                                      )
                            }
                            error={errorInfo}
                            defaultValue={(value || defaultValue) as string}
                            placeholder={field.placeHolder}
                            readOnly={booleanProp(field.readOnly)}
                            type={"email"}
                            className={"kms-ds-site-registration-email-field"}
                        />
                    );
                }
                case FieldType.select:
                    return (
                        <Controller
                            key={field.id}
                            name={"registrationinfo." + [field.name]}
                            control={control}
                            rules={noWhitespacesValidation({
                                required: field.mandatory,
                                maxLength: field.maxLength,
                            })}
                            defaultValue={value || defaultValue || ""}
                            render={({
                                field: { onChange, onBlur, value, ref },
                            }) => (
                                <SelectField
                                    id={field.id}
                                    title={field.displayName}
                                    ref={ref}
                                    onChange={onChange}
                                    onBlur={onBlur}
                                    value={value || defaultValue || ""}
                                    placeholder={field.placeHolder}
                                    options={field.options || []}
                                    error={errorInfo}
                                    errorMessage={errorMessage}
                                    createable={booleanProp(field.createable)}
                                    required={field.mandatory}
                                    isDisabled={booleanProp(field.readOnly)}
                                    autocompleteMatch={true}
                                    className={
                                        "kms-ds-site-registration-select-field"
                                    }
                                />
                            )}
                        />
                    );

                case FieldType.registrationType:
                    return (
                        <Controller
                            key={field.id}
                            name={"registrationinfo." + [field.name]}
                            control={control}
                            rules={noWhitespacesValidation({
                                required: field.mandatory,
                                maxLength: field.maxLength,
                            })}
                            defaultValue={value || defaultValue || ""}
                            render={({
                                field: { onChange, onBlur, value, ref },
                            }) => (
                                <RegistrationTypeField
                                    id={field.id}
                                    title={field.displayName}
                                    ref={ref}
                                    onChange={onChange}
                                    onBlur={onBlur}
                                    value={value || defaultValue || ""}
                                    options={field.options || []}
                                    error={errorInfo}
                                    errorMessage={errorMessage}
                                    required={field.mandatory}
                                    isDisabled={booleanProp(field.readOnly)}
                                    className={
                                        "kms-ds-site-registration-registrationType-field"
                                    }
                                />
                            )}
                        />
                    );
                case FieldType.checkbox:
                    return (
                        <Controller
                            key={field.id}
                            control={control}
                            rules={{ required: field.mandatory }}
                            name={"registrationinfo." + [field.name]}
                            defaultValue={booleanProp(
                                (value || defaultValue) as string | boolean
                            )}
                            render={({ field: { onChange, value, ref } }) => (
                                <CheckboxField
                                    id={field.id}
                                    name={field.id}
                                    title={field.displayName}
                                    ref={ref}
                                    onChange={onChange}
                                    checked={booleanProp(value ?? defaultValue)}
                                    disabled={booleanProp(field.readOnly)}
                                    error={errorInfo}
                                    mandatoryText={errorMessage}
                                    required={field.mandatory}
                                    className={
                                        "kms-ds-site-registration-checkbox-field"
                                    }
                                />
                            )}
                        />
                    );
                case FieldType.radio:
                    return (
                        <Controller
                            key={field.id}
                            control={control}
                            rules={{ required: field.mandatory }}
                            name={"registrationinfo." + [field.name]}
                            defaultValue={value || defaultValue}
                            render={({ field: { onChange, value, ref } }) => (
                                <RadioButtonField
                                    name={field.id}
                                    id={field.id}
                                    title={field.displayName}
                                    ref={ref}
                                    onChange={onChange}
                                    value={value || defaultValue}
                                    options={field.options!}
                                    disabled={booleanProp(field.readOnly)}
                                    error={errorInfo}
                                    mandatoryText={errorMessage}
                                    required={field.mandatory}
                                    className={
                                        "kms-ds-site-registration-radio-field"
                                    }
                                />
                            )}
                        />
                    );
                case FieldType.multitag:
                case FieldType.usertag:
                    return (
                        <Controller
                            key={field.id}
                            name={"registrationinfo." + [field.name]}
                            control={control}
                            rules={noEmptyArrayValidation({
                                required: field.mandatory,
                            })}
                            defaultValue={value || defaultValue || null}
                            render={({ field: { onChange, value, ref } }) => (
                                <TagSelectionField
                                    id={field.id}
                                    ref={ref}
                                    label={field.displayName}
                                    instructionsText={field.multitagInstruction}
                                    onChange={onChange}
                                    options={field.options}
                                    defaultValues={value || defaultValue}
                                    required={field.mandatory}
                                    maxSelection={field.tagsLimit}
                                    error={errorInfo}
                                    className={
                                        field.fieldType === FieldType.multitag
                                            ? "kms-ds-site-registration-multitag-field"
                                            : "kms-ds-site-registration-usertag-field"
                                    }
                                />
                            )}
                        />
                    );
                case FieldType.password:
                    return (
                        <Fragment key={`${field.id}-wrapper`}>
                            <PasswordField
                                key={field.id}
                                id={field.id}
                                title={translate("Password")}
                                {...register("registrationinfo.password", {
                                    validate: passwordValidation,
                                })}
                                required={field.mandatory}
                                error={
                                    errors.registrationinfo
                                        ?.password as FieldError
                                }
                                showErrors={
                                    !!passwordValue ||
                                    !!errors.registrationinfo?.password
                                }
                                autoComplete="new-password"
                                className={
                                    "kms-ds-site-registration-password-field"
                                }
                            />

                            <PasswordField
                                key={`${field.id}-confirmation`}
                                id={`${field.id}-confirmation`}
                                title={translate("Confirm Password")}
                                {...register("passwordRepeat", {
                                    required: true,
                                    validate: {
                                        matchFirstPassword: (value) =>
                                            value ===
                                                watch(
                                                    "registrationinfo.password"
                                                ) ||
                                            translate("Passwords do not match"),
                                    },
                                })}
                                required={field.mandatory}
                                errorMessage={
                                    errors.passwordRepeat?.types
                                        ?.matchFirstPassword
                                }
                                showErrors={
                                    !!errors.passwordRepeat?.types
                                        ?.matchFirstPassword
                                }
                                autoComplete="new-password"
                                className={
                                    "kms-ds-site-registration-password-field"
                                }
                            />
                        </Fragment>
                    );
                default:
                    return null;
            }
        });
    };

    // Does the field produce any DOM contents? (<input type="hidden"/> is a DOM content too)
    const isRenderedField = (field: RegistrationFieldInfo) => {
        return getVisibleDependency(field) && !booleanProp(field.hidden);
    };

    // Is the field visible in UI?
    const isVisibleField = (field: RegistrationFieldInfo) => {
        return isRenderedField(field) && field.fieldType !== FieldType.hidden;
    };

    // Does the section have fields that produce any DOM contents?
    const sectionHasRenderedFields = (fields: RegistrationFieldInfo[]) => {
        return fields.some(isRenderedField);
    };

    // Does the section have fields that are visible in UI?
    const sectionHasVisibleFields = (fields: RegistrationFieldInfo[]) => {
        return fields.some(isVisibleField);
    };

    // generate the form sections
    const generateSections = sections.map(
        (section: RegistrationSectionInfo, index: number) => {
            if (section.hidden || !sectionHasRenderedFields(section.fields)) {
                return null;
            }

            const fields = generateFields(section.fields);

            if (sectionHasVisibleFields(section.fields)) {
                // Render the section UI with the fields inside
                return (
                    <FormSection key={"section" + index} title={section.title}>
                        {fields}
                    </FormSection>
                );
            } else {
                // All fields are hidden, so don't render the section UI, only the fields themselves
                return <Fragment key={"section" + index}>{fields}</Fragment>;
            }
        }
    );

    return (
        <form
            className={clsx(className, "kms-ds-site-registration-form")}
            id={formId}
            onSubmit={handleSubmit(onSubmit)}
        >
            {generateSections}

            {registrationType === RegistrationType.NEW && (
                <CampaignValues register={register} />
            )}

            <div className={"kms-ds-site-registration-form-buttons"}>
                {registrationType === RegistrationType.NEW && recaptchaSiteKey && (
                    <RecaptchaContainer
                        className={"kms-ds-site-registration-form-recaptcha"}
                    >
                        <Controller
                            control={control}
                            rules={{ required: true }}
                            name="captcha"
                            defaultValue={null}
                            render={({ field: { onChange } }) => (
                                <ReCAPTCHA
                                    ref={captchaRef}
                                    sitekey={recaptchaSiteKey}
                                    theme={recaptchaTheme}
                                    onChange={onChange}
                                    onExpired={setCaptchaToken}
                                    hl={config.application.currentLocaleCode}
                                    size={recapthcaMode}
                                />
                            )}
                        />
                        <StyledCaptchaError aria-live="polite">
                            {errors.captcha && (
                                <FormFieldError>
                                    {translate("Captcha is required")}
                                </FormFieldError>
                            )}
                        </StyledCaptchaError>
                    </RecaptchaContainer>
                )}

                {!noSubmitButton && <StyledSubmitButton
                    fullWidth={!contentContext.fullWidth}
                    loading={processing}
                    disabled={ssoDomainsError || processing}
                    onClick={noop}
                    type="submit"
                    size={"large"}
                >
                    {registrationType === RegistrationType.NEW
                        ? translate("Register")
                        : translate("Update Details")}
                </StyledSubmitButton>}
            </div>
        </form>
    );
};

export default RegistrationForm;
